<template>
  <div>
    <h1 data-scroll-reveal>
      Things I've Built
    </h1>
    <article data-scroll-group data-scroll-step="110">
      <p>I like working through a problem and figuring out how the pieces could fit together. I enjoy trying different ideas and gradually building up a structure of my own.</p>
      <div v-if="!isMobileCollection" class="timeline-tools projects-timeline-tools collection-tools" data-scroll-reveal>
        <ViewModeControl
          :model-value="viewMode"
          label="Choose a project view"
          @update:model-value="setViewMode"
        />
        <label class="timeline-search" aria-label="Search projects">
          <input
            v-model="searchQuery"
            class="timeline-search-input"
            type="search"
            autocomplete="off"
            spellcheck="false"
            placeholder="Search projects"
            @keydown.esc.prevent="clearSearch"
          >
          <i class="fa fa-search timeline-search-icon" aria-hidden="true"></i>
        </label>
      </div>
      <div
        class="collection-view-stage projects-collection-view-stage"
        :class="{ 'is-view-transitioning': viewTransitionActive }"
      >
      <SectionMenu v-if="viewMode === 'expanded' && !isMobileCollection" :groups="chapterItems" :children="menuChildren" label="Project sections" />
      <ul v-if="viewMode === 'expanded'" class="timeline" data-scroll-group data-scroll-step="160" @click="handleTimelineClick">
        <li v-if="groupVisible('personal')" id="projects-personal" class="timeline-group-anchor" aria-hidden="true"></li>
        <li v-if="entryVisible('searchstory')" class="timeline-event">
          <label class="timeline-event-icon"></label>
          <div class="timeline-event-copy">
            <div class="timeline-event-head">
              <p class="timeline-event-thumbnail">{{ entryById('searchstory').period }}</p>
              <span class="timeline-event-tag">{{ groupLabel('personal') }}</span>
            </div>
            <h3 id="searchstory" v-html="highlightText(entryById('searchstory').title)"></h3>
            <h4>An AI-driven personal archive</h4>
            <p>Visitors can ask AI a question about my work, ideas or experiences. AI connects material from this site into a <AiViewTerm />, an illustrated article that answers their question and includes the original work. Each <AiViewTerm /> also becomes an exhibition visitors can walk through, and the <ArchiveTerm kind="gallery" /> brings shared exhibitions together.</p>
            <ProjectShowcase project-id="searchstory" :show-caption="false" :show-controls="false" class="timeline-project-showcase" />
            <ProjectEntryActions project-id="searchstory" />
            <SourceViewLinks source-id="projects:searchstory" />
          </div>
        </li>
        <li v-if="groupVisible('hackathon')" id="projects-hackathon" class="timeline-group-anchor" aria-hidden="true"></li>
        <li v-if="entryVisible('cathayflex')" class="timeline-event">
          <label class="timeline-event-icon"></label>
          <div class="timeline-event-copy">
            <div class="timeline-event-head">
              <p class="timeline-event-thumbnail">{{ entryById('cathayflex').period }}</p>
              <span class="timeline-event-tag">{{ groupLabel('hackathon') }}</span>
            </div>
            <h3 id="cathayflex" v-html="highlightText(entryById('cathayflex').title)"></h3>
            <h4 v-html="highlightText(entryById('cathayflex').subtitle)"></h4>
            <p v-html="highlightText(entryById('cathayflex').summary)"></p>
            <ProjectShowcase project-id="cathayflex" :show-caption="false" :show-controls="false" class="timeline-project-showcase" />
            <ProjectEntryActions project-id="cathayflex" />
            <SourceViewLinks source-id="projects:cathayflex" />
          </div>
        </li>
        <li v-if="groupVisible('professional')" id="projects-professional" class="timeline-group-anchor" aria-hidden="true"></li>
        <li v-if="entryVisible('slidepoise')" class="timeline-event">
          <label class="timeline-event-icon"></label>
          <div class="timeline-event-copy">
            <div class="timeline-event-head">
              <p class="timeline-event-thumbnail">{{ entryById('slidepoise').period }}</p>
              <span class="timeline-event-tag">{{ groupLabel(entryById('slidepoise').group) }}</span>
            </div>
            <h3 id="slidepoise" v-html="highlightText(entryById('slidepoise').title)"></h3>
            <h4 v-html="highlightText(entryById('slidepoise').subtitle)"></h4>
            <p v-html="highlightText(entryById('slidepoise').summary)"></p>
            <ProjectShowcase project-id="slidepoise" :show-caption="false" :show-controls="false" class="timeline-project-showcase" />
            <ProjectEntryActions project-id="slidepoise" />
            <SourceViewLinks source-id="projects:slidepoise" />
          </div>
        </li>
        <li v-if="entryVisible('pwcDocumentReview')" class="timeline-event">
          <label class="timeline-event-icon"></label>
          <div class="timeline-event-copy">
            <div class="timeline-event-head"><p class="timeline-event-thumbnail">{{ entryById('pwcDocumentReview').period }}</p><span class="timeline-event-tag">{{ groupLabel('professional') }}</span></div>
            <h3 id="pwcDocumentReview" v-html="highlightText(entryById('pwcDocumentReview').title)"></h3>
            <h4 v-html="highlightText(entryById('pwcDocumentReview').subtitle)"></h4>
            <p v-html="highlightText(entryById('pwcDocumentReview').summary)"></p>
            <ProjectEntryActions project-id="pwcDocumentReview" />
            <SourceViewLinks source-id="projects:pwcDocumentReview" />
          </div>
        </li>
        <li v-if="entryVisible('pwcItOps')" class="timeline-event">
          <label class="timeline-event-icon"></label>
          <div class="timeline-event-copy">
            <div class="timeline-event-head">
              <p class="timeline-event-thumbnail">{{ entryById('pwcItOps').period }}</p>
              <span class="timeline-event-tag">{{ groupLabel('professional') }}</span>
            </div>
            <h3 id="pwcItOps" v-html="highlightText(entryById('pwcItOps').title)"></h3>
            <h4 v-html="highlightText(entryById('pwcItOps').subtitle)"></h4>
            <h4>Associated with <a href="https://www.pwc.com.hk/" target="_blank" rel="noopener"><b>PwC</b></a></h4>
            <div class="image-wrapper">
              <img src="/assets/img/PwC.png" alt="PwC" style="height:6vh;">
            </div>
            <p v-html="highlightText(entryById('pwcItOps').summary)"></p>
            <ProjectEntryActions project-id="pwcItOps" />
            <SourceViewLinks source-id="projects:pwcItOps" />
          </div>
        </li>
        <li v-if="entryVisible('insurance')" class="timeline-event">
          <label class="timeline-event-icon"></label>
          <div class="timeline-event-copy">
            <div class="timeline-event-head">
              <p class="timeline-event-thumbnail">{{ entryById('insurance').period }}</p>
              <span class="timeline-event-tag">{{ groupLabel('professional') }}</span>
            </div>
            <h3 id="insurance" v-html="highlightText(entryById('insurance').title)"></h3>
            <h4 v-html="highlightText(entryById('insurance').subtitle)"></h4>
            <h4>Associated with <a href="https://www.hsbc.com/" target="_blank" rel="noopener"><b>HSBC</b></a></h4>
            <div class="image-wrapper">
              <img src="/assets/img/hsbc.svg" style="height:4.5vh;">
            </div>
            <p v-html="highlightText(entryById('insurance').summary)"></p>
            <ProjectEntryActions project-id="insurance" />
            <SourceViewLinks source-id="projects:insurance" />
          </div>
        </li>
        <li v-if="groupVisible('coursework')" id="projects-coursework" class="timeline-group-anchor" aria-hidden="true"></li>
        <li v-if="entryVisible('scrollytelling')" class="timeline-event">
          <label class="timeline-event-icon"></label>
          <div class="timeline-event-copy">
            <div class="timeline-event-head">
              <p class="timeline-event-thumbnail">{{ entryById('scrollytelling').period }}</p>
              <span class="timeline-event-tag">{{ groupLabel('coursework') }}</span>
            </div>
            <h3 id="scrollytelling" v-html="highlightText(entryById('scrollytelling').title)"></h3>
            <h4 v-html="highlightText(entryById('scrollytelling').subtitle)"></h4>
            <p v-html="highlightText(entryById('scrollytelling').summary)"></p>
            <FigureSequence :images="compactMedia(entryById('scrollytelling'))" :title="entryById('scrollytelling').title" />
            <ProjectEntryActions project-id="scrollytelling" />
            <SourceViewLinks source-id="projects:scrollytelling" />
          </div>
        </li>
        <li v-if="entryVisible('pwcRag')" class="timeline-event">
          <label class="timeline-event-icon"></label>
          <div class="timeline-event-copy">
            <div class="timeline-event-head">
              <p class="timeline-event-thumbnail">{{ entryById('pwcRag').period }}</p>
              <span class="timeline-event-tag">{{ groupLabel('professional') }}</span>
            </div>
            <h3 id="pwcRag" v-html="highlightText(entryById('pwcRag').title)"></h3>
            <h4 v-html="highlightText(entryById('pwcRag').subtitle)"></h4>
            <h4>Associated with <a href="https://www.pwc.com.hk/" target="_blank" rel="noopener"><b>PwC</b></a></h4>
            <div class="image-wrapper">
              <img src="/assets/img/PwC.png" style="height:6vh;">
            </div>
            <p v-html="highlightText(entryById('pwcRag').summary)"></p>
            <ProjectEntryActions project-id="pwcRag" />
            <SourceViewLinks source-id="projects:pwcRag" />
          </div>
        </li>
        <li v-if="entryVisible('stride')" class="timeline-event">
          <label class="timeline-event-icon"></label>
          <div class="timeline-event-copy">
            <div class="timeline-event-head">
              <p class="timeline-event-thumbnail">{{ entryById('stride').period }}</p>
              <span class="timeline-event-tag">{{ groupLabel('coursework') }}</span>
            </div>
            <h3 id="stride" v-html="highlightText(entryById('stride').title)"></h3>
            <h4 v-html="highlightText(entryById('stride').subtitle)"></h4>
            <p v-html="highlightText(entryById('stride').summary)"></p>
            <FigureSequence :images="compactMedia(entryById('stride'))" :title="entryById('stride').title" />
            <ProjectEntryActions project-id="stride" />
            <SourceViewLinks source-id="projects:stride" />
          </div>
        </li>
        <li v-if="entryVisible('dealtracker')" class="timeline-event">
          <label class="timeline-event-icon"></label>
          <div class="timeline-event-copy">
            <div class="timeline-event-head">
              <p class="timeline-event-thumbnail">{{ entryById('dealtracker').period }}</p>
              <span class="timeline-event-tag">{{ groupLabel('personal') }}</span>
            </div>
            <h3 id="dealtracker" v-html="highlightText(entryById('dealtracker').title)"></h3>
            <h4 v-html="highlightText(entryById('dealtracker').subtitle)"></h4>
            <p v-html="highlightText(entryById('dealtracker').summary)"></p>
            <FigureSequence :images="compactMedia(entryById('dealtracker'))" :title="entryById('dealtracker').title" />
            <ProjectEntryActions project-id="dealtracker" />
            <SourceViewLinks source-id="projects:dealtracker" />
          </div>
        </li>
        <li v-if="entryVisible('congentra')" class="timeline-event">
          <label class="timeline-event-icon"></label>
          <div class="timeline-event-copy">
            <div class="timeline-event-head">
              <p class="timeline-event-thumbnail">{{ entryById('congentra').period }}</p>
              <span class="timeline-event-tag">{{ groupLabel('personal') }}</span>
            </div>
            <h3 id="congentra" v-html="highlightText(entryById('congentra').title)"></h3>
            <h4 v-html="highlightText(entryById('congentra').subtitle)"></h4>
            <p v-html="highlightText(entryById('congentra').summary)"></p>
            <div class="scroll-container project proof-gallery">
              <img src="/assets/img/gallery-thumbs/congentra_0.png.webp" data-full-src="/assets/img/congentra_0.png" loading="lazy" decoding="async">
              <img src="/assets/img/gallery-thumbs/congentra_1.png.webp" data-full-src="/assets/img/congentra_1.png" loading="lazy" decoding="async">
              <img src="/assets/img/gallery-thumbs/congentra_2.png.webp" data-full-src="/assets/img/congentra_2.png" loading="lazy" decoding="async">
              <img src="/assets/img/gallery-thumbs/congentra_3.png.webp" data-full-src="/assets/img/congentra_3.png" loading="lazy" decoding="async">
              <img src="/assets/img/gallery-thumbs/congentra_4.png.webp" data-full-src="/assets/img/congentra_4.png" loading="lazy" decoding="async">
              <img src="/assets/img/gallery-thumbs/congentra_5.png.webp" data-full-src="/assets/img/congentra_5.png" loading="lazy" decoding="async">
              <img src="/assets/img/gallery-thumbs/congentra_6.png.webp" data-full-src="/assets/img/congentra_6.png" loading="lazy" decoding="async">
              <img src="/assets/img/gallery-thumbs/congentra_7.png.webp" data-full-src="/assets/img/congentra_7.png" loading="lazy" decoding="async">
              <img src="/assets/img/gallery-thumbs/congentra_8.png.webp" data-full-src="/assets/img/congentra_8.png" loading="lazy" decoding="async">
              <img src="/assets/img/gallery-thumbs/congentra_9.png.webp" data-full-src="/assets/img/congentra_9.png" loading="lazy" decoding="async">
            </div>
            <SourceViewLinks source-id="projects:congentra" />
          </div>
        </li>
        <li v-if="entryVisible('lexaday')" class="timeline-event">
          <label class="timeline-event-icon"></label>
          <div class="timeline-event-copy">
            <div class="timeline-event-head">
              <p class="timeline-event-thumbnail">{{ entryById('lexaday').period }}</p>
              <span class="timeline-event-tag">{{ groupLabel('personal') }}</span>
            </div>
            <h3 id="lexaday" v-html="highlightText(entryById('lexaday').title)"></h3>
            <h4 v-html="highlightText(entryById('lexaday').subtitle)"></h4>
            <p v-html="highlightText(entryById('lexaday').summary)"></p>
            <FigureSequence :images="compactMedia(entryById('lexaday'))" :title="entryById('lexaday').title" />
            <ProjectEntryActions project-id="lexaday" />
            <SourceViewLinks source-id="projects:lexaday" />
          </div>
        </li>
        <li v-if="entryVisible('pds')" class="timeline-event">
          <label class="timeline-event-icon"></label>
          <div class="timeline-event-copy">
            <div class="timeline-event-head">
              <p class="timeline-event-thumbnail">{{ entryById('pds').period }}</p>
              <span class="timeline-event-tag">{{ groupLabel('professional') }}</span>
            </div>
            <h3 id="pds" v-html="highlightText(entryById('pds').title)"></h3>
            <h4 v-html="highlightText(entryById('pds').subtitle)"></h4>
            <h4>Associated with <a href="https://psym-ds.github.io/" target="_blank" rel="noopener"><b>PDS Lab</b></a></h4>
            <p v-html="highlightText(entryById('pds').summary)"></p>
            <div class="scroll-container project proof-gallery">
              <img src="/assets/img/gallery-thumbs/PDS_Screenshot1.png.webp" data-full-src="/assets/img/PDS_Screenshot1.png" loading="lazy" decoding="async">
              <img src="/assets/img/gallery-thumbs/PDS_Screenshot2.png.webp" data-full-src="/assets/img/PDS_Screenshot2.png" loading="lazy" decoding="async">
              <img src="/assets/img/gallery-thumbs/PDS_Screenshot3.png.webp" data-full-src="/assets/img/PDS_Screenshot3.png" loading="lazy" decoding="async">
              <img src="/assets/img/gallery-thumbs/PDS_Screenshot4.png.webp" data-full-src="/assets/img/PDS_Screenshot4.png" loading="lazy" decoding="async">
            </div>
            <ProjectEntryActions project-id="pds" />
            <SourceViewLinks source-id="projects:pds" />
          </div>
        </li>
        <li v-if="entryVisible('early')" class="timeline-event">
          <label class="timeline-event-icon"></label>
          <div class="timeline-event-copy">
            <div class="timeline-event-head">
              <p class="timeline-event-thumbnail">{{ entryById('early').period }}</p>
              <span class="timeline-event-tag">{{ groupLabel('personal') }}</span>
            </div>
            <h3 id="early" v-html="highlightText(entryById('early').title)"></h3>
            <h4 v-html="highlightText(entryById('early').subtitle)"></h4>
            <p v-html="highlightText(entryById('early').summary)"></p>
            <FigureSequence :images="compactMedia(entryById('early'))" :title="entryById('early').title" />
            <ProjectEntryActions project-id="early" />
            <SourceViewLinks source-id="projects:early" />
          </div>
        </li>
      </ul>
      <CollectionBrowser
        v-else
        :entries="compactEntries"
        :mode="collectionMode"
        :featured-count="selectedProjectCount"
        :search-active="searchActive"
        featured-label="Selected"
        archive-label="All"
        :initial-open-id="restoredOpenId"
        :target-id="pendingRouteTargetId"
        @target-consumed="clearPendingRouteTarget"
      />
      <p v-if="searchActive && !hasSearchMatches" class="timeline-empty">No matching project entries.</p>
      </div>
    </article>
  </div>
</template>

<script>
import { supportsDesktopHover } from '../utils/desktopHover';
import { projectWebsiteTarget } from '../utils/viewSourceTarget';
import scrollRevealMixin from '../mixins/scrollRevealMixin';
import SectionMenu from './SectionMenu.vue';
import AiViewTerm from './ui/AiViewTerm.vue';
import ArchiveTerm from './ui/ArchiveTerm.vue';
import SourceViewLinks from './ui/SourceViewLinks.vue';
import ViewModeControl from './ui/ViewModeControl.vue';
import CollectionBrowser from './ui/CollectionBrowser.vue';
import ProjectShowcase from './ui/ProjectShowcase.vue';
import FigureSequence from './ui/FigureSequence.vue';
import { projectFigures } from '../data/projectFigures';
import ProjectEntryActions from './ui/ProjectEntryActions.vue';
import { visibleProjectEntries as projectEntries, selectedProjectCount, projectResourceLinks } from '../data/projects';
import { acquireInteractiveCursorLock } from '../utils/interactiveCursorLock';
import { consumeCollectionReturn } from '../utils/askSession';

export default {
  name: 'ProjectsTab',
  components: { ArchiveTerm, AiViewTerm, SourceViewLinks, SectionMenu, ViewModeControl, CollectionBrowser, FigureSequence, ProjectShowcase, ProjectEntryActions },
  mixins: [scrollRevealMixin],
  data() {
    const collectionReturn = consumeCollectionReturn();
    return {
      selectedProjectCount,
      searchQuery: '',
      viewMode: collectionReturn?.mode === 'grid' ? 'grid' : 'index',
      viewTransitionActive: false,
      isMobileCollection: typeof window !== 'undefined' && window.matchMedia('(max-width: 720px)').matches,
      pendingRouteTargetId: '',
      restoredOpenId: collectionReturn?.openId || '',
      restoringCollectionState: Boolean(collectionReturn),
      projectEntries
    };
  },
  computed: {
    collectionMode() { return this.isMobileCollection ? 'index' : this.viewMode; },
    searchTerms() {
      return this.searchQuery
        .toLowerCase()
        .split(/[\s,.;:!?()[\]{}"']+/)
        .map((term) => term.trim())
        .filter((term) => term.length >= 2);
    },
    searchActive() {
      return this.searchTerms.length > 0;
    },
    routeTargetId() {
      const raw = (this.$route && this.$route.hash ? this.$route.hash.replace(/^#/, '') : '')
        || (this.$route && this.$route.query ? this.$route.query.to : '')
        || '';
      try {
        return decodeURIComponent(raw);
      } catch (error) {
        return raw;
      }
    },
    hasSearchMatches() {
      return this.projectEntries.some((entry) => this.entryMatches(entry.id));
    },
    visibleProjectEntries() {
      return this.projectEntries.filter((entry) => this.entryMatches(entry.id));
    },
    compactEntries() {
      return this.visibleProjectEntries.map((entry) => ({
        id: entry.id,
        showcase: ['slidepoise', 'searchstory', 'cathayflex'].includes(entry.id),
        title: entry.title,
        subtitle: entry.subtitle,
        preview: entry.preview,
        body: this.projectDetailBody(entry),
        category: this.groupLabel(entry.group),
        period: entry.period,
        route: entry.route && entry.route !== '/projects' ? entry.route : null,
        externalPage: entry.id === 'slidepoise' || /^https?:\/\//.test(entry.route || ''),
        action: entry.cta || 'Open project',
        images: this.compactMedia(entry),
        galleryClass: this.projectGalleryClass(entry.id),
        lab: this.projectAssociation(entry.id),
        extraLinks: this.projectLinks(entry.id),
        sourceId: `projects:${entry.id}`,
        kind: 'project'
      }));
    },
    chapterItems() {
      return [
        { key: 'personal', id: 'projects-personal', label: 'Personal', title: 'On my own time' },
        { key: 'professional', id: 'projects-professional', label: 'Professional', title: 'Professional and lab work' },
        { key: 'hackathon', id: 'projects-hackathon', label: 'Hackathon' },
        { key: 'coursework', id: 'projects-coursework', label: 'Coursework' }
      ].filter((item) => this.groupVisible(item.key));
    },
    menuChildren() {
      const out = {};
      for (const item of this.chapterItems) {
        out[item.key] = this.projectEntries
          .filter((entry) => entry.group === item.key && this.entryMatches(entry.id))
          .map((entry) => ({
            id: entry.id,
            label: entry.title,
            subtitle: entry.subtitle || entry.summary || '',
            date: entry.period,
          }));
      }
      return out;
    }
  },
  watch: {
    '$route.hash'() {
      this.handleRouteTargetArrival();
    },
    '$route.query.to'() {
      this.handleRouteTargetArrival();
    },
    searchQuery() {
      this.refreshScrollReveal();
    }
  },
  mounted() {
    try {
      const saved = window.localStorage.getItem('projects-view-mode');
      if (!this.restoringCollectionState && ['grid', 'index'].includes(saved)) this.viewMode = saved;
    } catch (error) {
      // Browsing preferences remain optional when storage is unavailable.
    }
    this.setupCollectionViewport();
    this.handleRouteTargetArrival();
  },
  unmounted() {
    this.teardownCollectionViewport();
  },
  methods: {
    handleRouteTargetArrival() {
      this.pendingRouteTargetId = this.routeTargetId;
    },
    clearPendingRouteTarget(targetId) {
      if (this.pendingRouteTargetId === targetId) this.pendingRouteTargetId = '';
    },
    setupCollectionViewport() {
      this._collectionMobileMedia = window.matchMedia('(max-width: 720px)');
      this._handleCollectionViewport = (event) => {
        this.isMobileCollection = event.matches;
      };
      this._handleCollectionViewport(this._collectionMobileMedia);
      if (this._collectionMobileMedia.addEventListener) {
        this._collectionMobileMedia.addEventListener('change', this._handleCollectionViewport);
      } else {
        this._collectionMobileMedia.addListener(this._handleCollectionViewport);
      }
    },
    teardownCollectionViewport() {
      if (!this._collectionMobileMedia || !this._handleCollectionViewport) return;
      if (this._collectionMobileMedia.removeEventListener) {
        this._collectionMobileMedia.removeEventListener('change', this._handleCollectionViewport);
      } else {
        this._collectionMobileMedia.removeListener(this._handleCollectionViewport);
      }
    },
    escapeHtml(text) {
      return String(text == null ? '' : text)
        .split('&').join('&amp;')
        .split('<').join('&lt;')
        .split('>').join('&gt;')
        .split(String.fromCharCode(34)).join('&quot;')
        .split("'").join('&#39;');
    },
    escapeRegExp(text) {
      return String(text).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    highlightText(text) {
      const escaped = this.escapeHtml(text);
      const terms = [...new Set(this.searchTerms)]
        .sort((a, b) => b.length - a.length)
        .map((term) => this.escapeRegExp(term));
      if (!terms.length) return escaped;

      const re = new RegExp(`(${terms.join('|')})`, 'gi');
      return escaped.replace(re, '<mark>$1</mark>');
    },
    entryById(id) {
      return this.projectEntries.find((entry) => entry.id === id);
    },
    compactMedia(entry) {
      return projectFigures(entry);
    },
    projectDetailBody(entry) {
      return (entry.overview || entry.summary || '').split(/\n\n+/)
        .map(paragraph => `<p>${this.escapeHtml(paragraph)}</p>`).join('');
    },
    projectGalleryClass(id) {
      return {
        searchstory: 'collection-detail-hero',
        slidepoise: 'collection-detail-hero',
        dealtracker: 'proof-gallery-lg',
        stride: 'proof-gallery-rounded',
        lexaday: 'collection-detail-gallery-compact'
      }[id] || '';
    },
    projectAssociation(id) {
      return {
        insurance: { name: 'HSBC', url: 'https://www.hsbc.com/', logo: '/assets/img/hsbc.svg', logoHeight: '4.5vh' },
        pwcRag: { name: 'PwC', url: 'https://www.pwc.com.hk/', logo: '/assets/img/PwC.png', logoHeight: '6vh' },
        pwcDocumentReview: { name: 'PwC', url: 'https://www.pwc.com.hk/', logo: '/assets/img/PwC.png', logoHeight: '6vh' },
        pwcItOps: { name: 'PwC', url: 'https://www.pwc.com.hk/', logo: '/assets/img/PwC.png', logoHeight: '6vh' },
        pds: { name: 'PDS Lab', url: 'https://psym-ds.github.io/' }
      }[id] || null;
    },
    projectLinks(id) { return projectResourceLinks[id] || []; },
    entryMatches(id) {
      const entry = this.entryById(id);
      if (!entry) return false;
      const text = [
        entry.title,
        entry.subtitle,
        entry.summary,
        ...(entry.details || []),
        entry.text
      ].join(' ').toLowerCase();
      return this.searchTerms.every((term) => text.includes(term));
    },
    groupMatches(groupKey) {
      return this.projectEntries
        .filter((entry) => entry.group === groupKey)
        .some((entry) => this.entryMatches(entry.id));
    },
    groupVisible(groupKey) {
      if (!this.searchActive) return true;
      return this.groupMatches(groupKey);
    },
    entryVisible(id) {
      return this.entryMatches(id);
    },
    handleTimelineClick(event) {
      if (!supportsDesktopHover()) return;
      const card = event.target.closest('.timeline-event');
      if (!card) return;
      // Inner links, buttons (e.g. the Seen-in-Views trigger), and zoomable
      // images keep their own behavior; anywhere else opens the project page.
      if (event.target.closest('a, button, [data-full-src]')) return;
      const h3 = card.querySelector('h3');
      const entry = h3 && h3.id ? this.entryById(h3.id) : null;
      if (!entry || !entry.route || entry.route === '/projects') return;
      const destination = projectWebsiteTarget(entry.route);
      if (destination?.href) {
        window.open(destination.href, '_blank', 'noopener');
        return;
      }
      this.$router.push(entry.route).catch(() => {});
    },
    groupLabel(groupKey) {
      const item = this.chapterItems.find((entry) => entry.key === groupKey);
      return item ? item.label : groupKey;
    },
    async setViewMode(mode) {
      if (this.isMobileCollection || !['grid', 'index'].includes(mode) || mode === this.viewMode) return;
      try {
        window.localStorage.setItem('projects-view-mode', mode);
      } catch (error) {
        // The selected view still works for this visit without persistence.
      }
      const applyMode = async () => {
        this.viewMode = mode;
        await this.$nextTick();
        this.refreshScrollReveal();
      };
      const reduceMotion = window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!document.startViewTransition || reduceMotion) {
        applyMode();
        return;
      }
      const releaseCursorLock = acquireInteractiveCursorLock();
      this.viewTransitionActive = true;
      const transitionToken = (this._viewTransitionToken || 0) + 1;
      this._viewTransitionToken = transitionToken;
      await this.$nextTick();
      let transition;
      try {
        transition = document.startViewTransition(applyMode);
      } catch (error) {
        releaseCursorLock();
        this.viewTransitionActive = false;
        await applyMode();
        return;
      }
      transition.finished.catch(() => {}).finally(() => {
        releaseCursorLock();
        if (this._viewTransitionToken === transitionToken) this.viewTransitionActive = false;
      });
    },
    chapterId(groupKey) {
      const item = this.chapterItems.find((entry) => entry.key === groupKey);
      return item ? item.id : '';
    },
    // Toggle disabled: groups stay expanded.
    // toggleGroup(groupKey) {
    //   if (this.searchActive) return;
    //   this.expanded = {
    //     ...this.expanded,
    //     [groupKey]: !this.expanded[groupKey]
    //   };
    //   this.refreshScrollReveal();
    // },
    clearSearch() {
      this.searchQuery = '';
    }
  }
}
</script>

<style scoped>
.collection-tools {
  justify-content: space-between;
}

@media (max-width: 720px) {
  .collection-tools {
    display: flex;
    width: 100%;
  }

  .collection-tools .timeline-search {
    display: none;
  }
}

.timeline-project-showcase { margin: 1.2rem 0; }

.timeline-project-hero {
  display: block;
  width: 100%;
  height: auto;
  margin: 1.1rem 0 1.2rem;
  cursor: zoom-in;
}

.timeline-project-hero-half { width: 50%; }

</style>
